import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";

interface IntegrationData {
  id?: string;
  account_id: string;
  token_openai: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

const IntegrationsTab: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [tokenOpenAI, setTokenOpenAI] = useState("");

  // Carregar dados existentes
  const loadIntegrationData = async () => {
    if (!user || !supabase) return;

    try {
      setLoadingData(true);
      
      const { data, error } = await supabase
        .from('account_integrations')
        .select('*')
        .eq('account_id', user.account_id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
        throw error;
      }

      if (data) {
        setTokenOpenAI(data.token_openai || "");
        console.log('✅ [IntegrationsTab] Dados carregados:', data);
      }
    } catch (error) {
      console.error('❌ [IntegrationsTab] Erro ao carregar dados:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar configurações de integração' });
    } finally {
      setLoadingData(false);
    }
  };

  // Salvar token OpenAI
  const handleSaveOpenAI = async () => {
    if (!user || !supabase) return;

    setLoading(true);
    setMessage(null);

    try {
      const integrationData: IntegrationData = {
        account_id: user.account_id,
        token_openai: tokenOpenAI.trim() || null,
      };

      const { error } = await supabase
        .from('account_integrations')
        .upsert(integrationData, {
          onConflict: 'account_id',
          ignoreDuplicates: false
        });

      if (error) throw error;

      setMessage({ type: 'success', text: 'Token OpenAI salvo com sucesso!' });
      console.log('✅ [IntegrationsTab] Token OpenAI salvo com sucesso');
    } catch (error) {
      console.error('❌ [IntegrationsTab] Erro ao salvar token:', error);
      setMessage({ type: 'error', text: 'Erro ao salvar token OpenAI' });
    } finally {
      setLoading(false);
    }
  };

  // Carregar dados ao montar o componente
  useEffect(() => {
    loadIntegrationData();
  }, [user]);

  if (loadingData) {
    return (
      <div className="space-y-4 @container/inte">
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-4 border-neutral-600 border-t-neutral-50 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 @container/inte">
      {message && (
        <div className={`p-3 rounded-md ${
          message.type === 'success' 
            ? 'bg-green-500/20 border border-green-500/30 text-green-300' 
            : 'bg-red-500/20 border border-red-500/30 text-red-300'
        }`}>
          {message.text}
        </div>
      )}

      <div className="card flex flex-col @sm/inte:flex-row items-center gap-4 p-[17px] rounded-[6px] bg-neutral-600">
        <img src="/integrac.svg" alt="" className="w-10 h-10" />
        <div className="space-y-1 text-center @sm/inte:text-left">
          <h3 className="font-semibold text-[14px] text-[#FAFAFA]">Integrações</h3>
          <p className="text-[14px] text-[#FAFAFA]">
            Configure suas integrações com serviços externos para expandir as funcionalidades do sistema.
          </p>
        </div>
        <Button className="bg-[#E4E4E7] min-w-[120px] mt-6 self-center @sm/inte:self-end">
          Documentação
        </Button>
      </div>

      <div className="card flex flex-col items-center @sm/inte:flex-row gap-4 p-[17px] rounded-[6px] bg-neutral-600">
        <img src="/integrac.svg" alt="" />
        <div className="space-y-1 text-center @sm/inte:text-left flex-1">
          <h3 className="font-semibold text-[14px] text-[#FAFAFA]">Chat GPT</h3>
          <p className="text-[14px] text-[#FAFAFA]">
            Configure sua chave de API do OpenAI para habilitar funcionalidades de IA nos assistentes.
          </p>
          <Input
            placeholder="sk-..."
            id="chatapi"
            value={tokenOpenAI}
            onChange={(e) => setTokenOpenAI(e.target.value)}
            className="bg-neutral-600 mt-3 text-[14px] placeholder:text-[#71717A] text-[#71717A] min-h-[36px] border focus:ring-white border-[#3F3F46]"
            type="password"
          />
        </div>
        <Button 
          onClick={handleSaveOpenAI}
          disabled={loading}
          className="bg-[#E4E4E7] min-w-[120px] mt-6 self-center @sm/inte:self-end"
        >
          {loading ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
    </div>
  );
};

export default IntegrationsTab;